import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ViewAnimalStatus.css';
import AlkidmatLogo from '../../assets/Alkidmatlogo.png';       // put your Alkidmat logo here
import JamatIslamiLogo from '../../assets/jilogo.jpg'; // put your Jamat-e-Islami logo here

// Use normalized keys for status mapping (lowercase, hyphenated)
const STATUS_MAPPING = {
  'pending': 'Pending',
  'slaughtered': 'Slaughtered',
  'in-process': 'In-Process',
  'trnasported' :'transported',
  'ready-for-delivery': 'Ready-For-Delivery',
  'delivered': 'Delivered',
};

const getStatusClassName = (status) => {
  return status?.toString().trim().toLowerCase().replace(/\s+/g, '-') || 'unknown';
};

export function ViewAnimalStatus() {
  const [allAnimals, setAllAnimals] = useState([]); // all data from backend
  const [filteredAnimals, setFilteredAnimals] = useState([]); // filtered data shown
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    animal_number: '',
    animal_type: '',
    category: '',
    day: '',
    status: 'all',
  });

  // Fetch all data once on component mount
  useEffect(() => {
    const fetchAllAnimals = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get('https://apiiq.jamamasjidquba.com/api/animals/animalstatus');
        setAllAnimals(response.data.data || []);
        setFilteredAnimals(response.data.data || []);
      } catch (err) {
        console.error('Error fetching animals:', err);
        setError('Failed to fetch animal data.');
      } finally {
        setLoading(false);
      }
    };

    fetchAllAnimals();
  }, []);

  // Apply filters client-side whenever filters or allAnimals change
  useEffect(() => {
    let filtered = [...allAnimals];

    if (filters.animal_number.trim() !== '') {
      filtered = filtered.filter(animal =>
        String(animal.animal_no).toLowerCase().includes(filters.animal_number.toLowerCase())
      );
    }
    if (filters.animal_type.trim() !== '') {
      filtered = filtered.filter(animal =>
        (animal.animal_cat || '').trim().toLowerCase() === filters.animal_type.trim().toLowerCase()
      );
    }
    if (filters.category.trim() !== '') {
      filtered = filtered.filter(animal =>
        (animal.animal_subcat || '').trim().toLowerCase() === filters.category.trim().toLowerCase()
      );
    }
    if (filters.day.trim() !== '') {
      filtered = filtered.filter(animal =>
        String(animal.day) === filters.day
      );
    }
    if (filters.status !== 'all') {
      // normalize status filter value and compare with normalized animal.status
      const normalizedFilterStatus = filters.status.trim().toLowerCase();
      filtered = filtered.filter(animal =>
        getStatusClassName(animal.status) === normalizedFilterStatus
      );
    }

    setFilteredAnimals(filtered);
  }, [filters, allAnimals]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="view-animal-status-container">
        <div className="header-bar">
        <img src={AlkidmatLogo} alt="Alkidmat Logo" className="header-logo left-logo" />
        <h1 className="header-title">Animal Status Dashboard</h1>
        <img src={JamatIslamiLogo} alt="Jamat-e-Islami Logo" className="header-logo right-logo"  />
      </div>
      {/* Filters */}
      <div className="filters-container">
        <input
          type="text"
          placeholder="Search by Animal Number"
          className="filter-input"
          value={filters.animal_number}
          onChange={(e) => handleFilterChange('animal_number', e.target.value)}
        />
        <select
          value={filters.animal_type}
          onChange={(e) => handleFilterChange('animal_type', e.target.value)}
          className="filter-select"
        >
          <option value="">All Types</option>
          <option value="cow">Cow</option>
          <option value="goat">Goat</option>
        </select>
        <select
          value={filters.category}
          onChange={(e) => handleFilterChange('category', e.target.value)}
          className="filter-select"
        >
          <option value="">All Categories</option>
          <option value="premium">Premium</option>
          <option value="ijtimai">Ijtimai</option>
          <option value="assan">Assan</option>
        </select>
        <select
          value={filters.day}
          onChange={(e) => handleFilterChange('day', e.target.value)}
          className="filter-select"
        >
          <option value="">All Days</option>
          <option value="1">Day 1</option>
          <option value="2">Day 2</option>
          <option value="3">Day 3</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="slaughtered">Slaughtered</option>
          <option value="in-process">In-Process</option>
          <option value="Transported">Transported</option>
          <option value="ready-for-delivery">Ready-For-Delivery</option>
          <option value="delivered">Delivered</option>
        </select>
      </div>

      {/* Messages */}
      {loading && <div className="loading-indicator">Loading...</div>}
      {error && <div className="error-message">{error}</div>}

      {/* Table */}
      <div className="table-container table-responsive">
        <table className="animal-status-table">
          <thead className='text-center'>
            <tr>
              <th>ID</th>
              <th>Animal Number</th>
              <th>Animal Type</th>
              <th>Category</th>
              <th>Day</th>
              <th>Status</th>
              <th>Last Updated</th>
            </tr>
          </thead>
          <tbody>
            {filteredAnimals.length > 0 ? (
              filteredAnimals.map((animal, index) => {
                const normalizedStatus = getStatusClassName(animal.status);
                return (
                  <tr key={`${animal.id}-${animal.animal_no}`}>
                    <td>{index + 1}</td> {/* Serial number starting from 1 */}
                    <td className='text-center'><strong>{animal.animal_no}</strong></td>
                    <td>{animal.animal_cat}</td>
                    <td>{animal.animal_subcat}</td>
                    <td>{animal.day}</td>
                    <td>
                      <span className={`status-badge ${normalizedStatus}`}>
                        {STATUS_MAPPING[normalizedStatus] || animal.status || 'Unknown'}
                      </span>
                    </td>
                    <td>{new Date( animal.created_at).toLocaleString()}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  {loading ? 'Loading...' : 'No animals found matching your criteria'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
